import React from 'react';
import { Users, Building2, Calendar, FileCheck, FileText, Award, FileDown, Star, ChevronDown, Mail, Phone, Globe, Shield, Hash, MapPin, User, Eye, Edit, Calculator, MoreHorizontal, Upload, FolderOpen, Loader2, QrCode, Coins, Download, UserPlus, Send, UserCog, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../ui/dropdown-menu';
import { cn } from '../../lib/utils';
import { StatusBadge, RCDSBadge, FTPEBadge, DOSBadge, MemberAvatar } from './badges';
import { formatCodeNAF, getCodeNAFLabel } from '../../utils/codeNaf';
import MemberDocsInline from './MemberDocsInline';
import { HistoriqueMandats } from '../sepa/HistoriqueMandats';
import { getToken } from '../../services/tokenService';

import { CoppamRowBadge } from './CoppamRowBadge';

export default function MembreRow({
  membre, getMid, expandedIds, toggleExpand, canDelete, selectedIds, toggleSelect,
  showEntreprise, handleStatusChange, updatingStatusId, showRCDS, showFTPE, showDOS,
  userRole, setFtpeDateMembre, docsSummary, coppamResume = {}, canChangeStatus, showActions,
  onView, setEditContactMembre, onViewDocuments, setSimsDOSMembre, setUploadDocMembre,
  setQualifsMembre, setSubDocsMembre, setDispositifsMembre, handleGenerateReleve,
  generatingReleveId, handleGenerateReleveFtpe, generatingReleveFtpeId,
  setRemiseSolidaireMembre, setSendFTPEMembre, onFTPEAction, onCreateEstimation,
  onSendEmail, onImpersonate, setAssignCreateurMembre, onDelete, authToken,
}) {
  const mid = getMid(membre);
  const isExpanded = expandedIds.has(mid);
  return (
              <div key={mid} className={cn("rounded-lg border transition-all duration-150", isExpanded ? "border-blue-200 shadow-sm bg-white" : "border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm")} data-testid={`membre-row-${mid}`}>
                {/* Ligne compacte cliquable */}
                <div role="button" tabIndex={0} onKeyDown={(e) => { if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); toggleExpand(mid); } }}
                  onClick={() => toggleExpand(mid)}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-left cursor-pointer select-none"
                  data-testid={`membre-toggle-${mid}`}
                >
                  {canDelete && (
                    <span onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={selectedIds.has(mid)}
                        onCheckedChange={() => toggleSelect(mid)}
                        className="data-[state=checked]:bg-red-500 data-[state=checked]:border-red-500"
                        data-testid={`select-membre-${mid}`}
                        aria-label={`Selectionner ${membre.prenom} ${membre.nom}`}
                      />
                    </span>
                  )}
                  <MemberAvatar membre={membre} />
                  <div className="min-w-0 flex-1">
                    <p className={cn("font-medium text-sm truncate transition-colors", isExpanded ? "text-blue-700" : "text-gray-900")} title={`${membre.prenom || ''} ${membre.nom || ''}`}>
                      {membre.prenom} {membre.nom}
                    </p>
                    <p className="text-[10px] text-gray-400 font-mono truncate">{membre.numero_membre || membre.id?.substring(0, 8)}</p>
                  </div>
                  {showEntreprise && membre.entreprise && (
                    <div className="hidden sm:flex items-center gap-1 text-xs text-gray-500 truncate max-w-[160px]" title={membre.entreprise}>
                      <Building2 className="h-3 w-3 text-gray-400 shrink-0" />
                      <span className="truncate">{membre.entreprise}</span>
                    </div>
                  )}
                  <StatusBadge status={membre.status} membre={membre} canChangeStatus={false} onStatusChange={handleStatusChange} isUpdating={updatingStatusId === mid} />
                  {showRCDS && <span className="hidden md:block"><RCDSBadge membre={membre} /></span>}
                  {/* V36.51 — FTPE Badge TOUJOURS visible (avant: hidden md:block, masquait sur mobile) */}
                  {showFTPE && <FTPEBadge membre={membre} />}
                  {showDOS && <span className="hidden lg:block"><DOSBadge membre={membre} /></span>}
                  <CoppamRowBadge resume={coppamResume[mid] || coppamResume[membre.id]} mid={mid} />
                  {/* V36.51 — Bouton rapide "Modifier date d'adhésion FTPE" (Super Admin / Admin),
                      directement visible dans la ligne — avant: enfoui dans le menu dropdown. */}
                  {['super_admin', 'admin'].includes(userRole) && membre.ftpe_actif && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setFtpeDateMembre(membre); }}
                      className="hidden sm:inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] bg-amber-50 hover:bg-amber-100 text-amber-700 border border-amber-200 transition-colors flex-shrink-0"
                      title={`Modifier la date d'adhésion FTPE${membre.date_adhesion_ftpe ? ` (actuelle: ${new Date(membre.date_adhesion_ftpe).toLocaleDateString('fr-FR')})` : ''}`}
                      data-testid={`btn-edit-ftpe-date-${mid}`}
                    >
                      <Calendar className="h-3 w-3" />
                      <span className="hidden xl:inline">Date FTPE</span>
                    </button>
                  )}
                  {docsSummary[mid] && (
                    <div className="flex items-center gap-0.5 flex-shrink-0" data-testid={`doc-badges-${mid}`}>
                      {docsSummary[mid].types.attestation_signee && <span className="hidden md:inline-flex w-5 h-5 rounded bg-blue-100 items-center justify-center" title={`Attestation signee (${docsSummary[mid].types.attestation_signee})`}><FileCheck className="w-3 h-3 text-blue-600" /></span>}
                      {docsSummary[mid].types.conditions_generales_particulieres_signees && <span className="hidden md:inline-flex w-5 h-5 rounded bg-emerald-100 items-center justify-center" title={`CG/CP signees (${docsSummary[mid].types.conditions_generales_particulieres_signees})`}><FileText className="w-3 h-3 text-emerald-600" /></span>}
                      {docsSummary[mid].types.attestation_ftpe_signee && <span className="hidden md:inline-flex w-5 h-5 rounded bg-amber-100 items-center justify-center" title={`Attestation FTPE signee (${docsSummary[mid].types.attestation_ftpe_signee})`}><Award className="w-3 h-3 text-amber-600" /></span>}
                      {docsSummary[mid].types.devis_ess && <span className="hidden md:inline-flex w-5 h-5 rounded bg-violet-100 items-center justify-center" title={`Devis ESS (${docsSummary[mid].types.devis_ess})`}><FileDown className="w-3 h-3 text-violet-600" /></span>}
                      {/* V36.51 — Certificat O'SCOP TOUJOURS visible (avant masqué par hidden lg:flex parent) */}
                      {docsSummary[mid].types.certificat_adhesion && <span className="inline-flex w-5 h-5 rounded bg-indigo-100 items-center justify-center" title={`Certificat d'adhésion O'SCOP (${docsSummary[mid].types.certificat_adhesion})`} data-testid={`badge-certificat-oscop-${mid}`}><Star className="w-3 h-3 text-indigo-600" /></span>}
                      {(docsSummary[mid].types.attestation_ftpe_generee) && <span className="hidden md:inline-flex w-5 h-5 rounded bg-orange-100 items-center justify-center" title={`Attestation FTPE generee (${docsSummary[mid].types.attestation_ftpe_generee})`}><Award className="w-3 h-3 text-orange-600" /></span>}
                    </div>
                  )}
                  <ChevronDown className={cn("h-4 w-4 text-gray-400 shrink-0 transition-transform duration-200", isExpanded && "rotate-180 text-blue-500")} />
                </div>

                {/* Panneau depliant */}
                {isExpanded && (
                  <div className="px-4 pb-4 pt-1 border-t border-gray-100 animate-in fade-in slide-in-from-top-1 duration-150" data-testid={`membre-details-${mid}`}>
                    <HistoriqueMandats membreId={mid} headers={{ Authorization: `Bearer ${getToken()}` }} />
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {/* Colonne 1: Contact & Identification */}
                      <div className="space-y-2">
                        <h4 className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Contact</h4>
                        <div className="space-y-1.5">
                          {membre.email && (
                            <a href={`mailto:${membre.email}`} className="flex items-center gap-2 text-sm text-gray-700 hover:text-blue-600 transition-colors">
                              <Mail className="h-3.5 w-3.5 text-gray-400" />
                              <span className="truncate">{membre.email}</span>
                            </a>
                          )}
                          {membre.telephone && (
                            <a href={`tel:${membre.telephone}`} className="flex items-center gap-2 text-sm text-gray-700 hover:text-blue-600 transition-colors">
                              <Phone className="h-3.5 w-3.5 text-gray-400" />
                              <span>{membre.telephone}</span>
                            </a>
                          )}
                          {membre.site_web && (
                            <a href={membre.site_web.startsWith('http') ? membre.site_web : `https://${membre.site_web}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-gray-700 hover:text-blue-600 transition-colors">
                              <Globe className="h-3.5 w-3.5 text-gray-400" />
                              <span className="truncate">{membre.site_web}</span>
                            </a>
                          )}
                          {!membre.email && !membre.telephone && <p className="text-sm text-gray-400">Aucun contact renseigne</p>}
                        </div>
                        {(membre.siret || membre.code_naf || membre.statut_juridique) && (
                          <div className="pt-1">
                            <h4 className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Identification professionnelle</h4>
                            {membre.siret && (
                              <p className="text-sm text-gray-700 flex items-center gap-1.5" data-testid={`siret-${membre.id}`}>
                                <Shield className="h-3.5 w-3.5 text-gray-400" />
                                <span className="font-mono text-xs">SIRET : {membre.siret}</span>
                              </p>
                            )}
                            {membre.code_naf && (
                              <p className="text-sm text-gray-700 flex items-start gap-1.5 mt-0.5" data-testid={`code-ape-${membre.id}`}>
                                <Hash className="h-3.5 w-3.5 text-gray-400 mt-0.5 shrink-0" />
                                <span className="text-xs">
                                  <span className="font-mono font-semibold">Code APE : {formatCodeNAF(membre.code_naf)}</span>
                                  {getCodeNAFLabel(membre.code_naf) && (
                                    <span className="text-slate-500 ml-1.5">— {getCodeNAFLabel(membre.code_naf)}</span>
                                  )}
                                </span>
                              </p>
                            )}
                            {membre.statut_juridique && (
                              <p className="text-sm text-gray-700 flex items-center gap-1.5 mt-0.5" data-testid={`statut-juridique-${membre.id}`}>
                                <Building2 className="h-3.5 w-3.5 text-gray-400" />
                                <span className="text-xs">Statut : {membre.statut_juridique}</span>
                              </p>
                            )}
                            {membre.date_creation_entreprise && (
                              <p className="text-sm text-gray-700 flex items-center gap-1.5 mt-0.5" data-testid={`date-creation-${membre.id}`}>
                                <Calendar className="h-3.5 w-3.5 text-gray-400" />
                                <span className="text-xs">Creation : {membre.date_creation_entreprise}</span>
                              </p>
                            )}
                            {membre.effectif && (
                              <p className="text-sm text-gray-700 flex items-center gap-1.5 mt-0.5" data-testid={`effectif-${membre.id}`}>
                                <Users className="h-3.5 w-3.5 text-gray-400" />
                                <span className="text-xs">Effectif : {membre.effectif}</span>
                              </p>
                            )}
                            {(membre.chiffre_affaires !== undefined && membre.chiffre_affaires !== null && membre.chiffre_affaires !== '') && (
                              <p className="text-sm text-gray-700 flex items-center gap-1.5 mt-0.5" data-testid={`ca-${membre.id}`}>
                                <Star className="h-3.5 w-3.5 text-gray-400" />
                                <span className="text-xs">Chiffre d'affaires : {parseFloat(membre.chiffre_affaires || 0).toLocaleString('fr-FR')} &euro;</span>
                              </p>
                            )}
                          </div>
                        )}
                        {(membre.adresse || membre.ville || membre.territoire) && (
                          <div className="pt-1">
                            <h4 className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Adresse & Territoire</h4>
                            {membre.adresse && (
                              <p className="text-sm text-gray-700 flex items-center gap-1.5">
                                <MapPin className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                                <span className="text-xs">{membre.adresse}</span>
                              </p>
                            )}
                            {(membre.code_postal || membre.ville) && (
                              <p className="text-sm text-gray-700 ml-5 text-xs">{[membre.code_postal, membre.ville].filter(Boolean).join(' ')}{membre.pays ? ` — ${membre.pays}` : ''}</p>
                            )}
                            {membre.territoire && (
                              <p className="text-sm text-gray-700 flex items-center gap-1.5 mt-0.5">
                                <Globe className="h-3.5 w-3.5 text-gray-400" />
                                <span className="text-xs">Territoire : {membre.territoire}</span>
                              </p>
                            )}
                          </div>
                        )}
                        {showEntreprise && (
                          <div className="pt-1">
                            <h4 className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Entreprise</h4>
                            <p className="text-sm text-gray-900 flex items-center gap-1.5">
                              <Building2 className="h-3.5 w-3.5 text-gray-400" />
                              {membre.entreprise || '-'}
                            </p>
                            {membre.siret && <p className="text-[11px] text-gray-400 font-mono ml-5">SIRET: {membre.siret}</p>}
                          </div>
                        )}
                      </div>

                      {/* Colonne 2: Dispositifs */}
                      <div className="space-y-2">
                        <h4 className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Dispositifs</h4>
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-gray-600">Statut</span>
                            <StatusBadge status={membre.status} membre={membre} canChangeStatus={canChangeStatus} onStatusChange={handleStatusChange} isUpdating={updatingStatusId === mid} />
                          </div>
                          {showRCDS && (
                            <div className="flex items-center justify-between">
                              <span className="text-xs text-gray-600">RCDS</span>
                              <RCDSBadge membre={membre} />
                            </div>
                          )}
                          {showFTPE && (
                            <div className="flex items-center justify-between">
                              <span className="text-xs text-gray-600">FTPE</span>
                              <FTPEBadge membre={membre} />
                            </div>
                          )}
                          {showDOS && (
                            <div className="flex items-center justify-between">
                              <span className="text-xs text-gray-600">DOS</span>
                              <DOSBadge membre={membre} />
                            </div>
                          )}
                        </div>
                        <div className="pt-1 space-y-1">
                          <div className="flex items-center gap-1.5 text-xs text-gray-500">
                            <Calendar className="h-3 w-3" />
                            <span>Cree le {membre.created_at || membre.date_creation ? new Date(membre.created_at || membre.date_creation).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' }) : '-'}</span>
                          </div>
                          <div className="flex items-center gap-1.5 text-xs text-gray-500">
                            <User className="h-3 w-3" />
                            <span>
                              Par {membre.created_by_name || 'Systeme'}
                              <Badge variant="outline" className="ml-1 text-[9px] px-1 py-0">
                                {membre.created_by_role === 'super_admin' ? 'Super Admin' : membre.created_by_role === 'admin' ? 'Admin' : membre.created_by_role === 'cooper' ? "Cooper's" : membre.created_by_role === 'expert' ? 'Expert' : 'Systeme'}
                              </Badge>
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Colonne 3: Actions — Layout pro avec hiérarchie visuelle */}
                      {showActions && (
                        <div className="space-y-3">
                          <h4 className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Actions</h4>

                          {/* Actions PRIMAIRES (toujours visibles, les + utilisées) */}
                          <div className="grid grid-cols-2 gap-2">
                            {onView && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => onView(membre)}
                                className="h-9 text-xs gap-1.5 justify-start font-medium border-slate-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 transition-all"
                                data-testid={`view-membre-${membre.id}`}
                              >
                                <Eye className="h-3.5 w-3.5 text-blue-500" /> Voir profil
                              </Button>
                            )}
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setEditContactMembre(membre)}
                              className="h-9 text-xs gap-1.5 justify-start font-medium border-slate-200 hover:border-amber-300 hover:bg-amber-50 hover:text-amber-700 transition-all"
                              data-testid={`edit-coordonnees-${membre.id}`}
                            >
                              <Edit className="h-3.5 w-3.5 text-amber-500" /> Coordonnées
                            </Button>
                            {onViewDocuments && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => onViewDocuments(membre)}
                                className="h-9 text-xs gap-1.5 justify-start font-medium border-slate-200 hover:border-indigo-300 hover:bg-indigo-50 hover:text-indigo-700 transition-all"
                                data-testid={`docs-membre-${membre.id}`}
                              >
                                <FileText className="h-3.5 w-3.5 text-indigo-500" /> Documents
                              </Button>
                            )}
                            {['super_admin', 'admin', 'cooper'].includes(userRole) && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setSimsDOSMembre(membre)}
                                className="h-9 text-xs gap-1.5 justify-start font-medium border-slate-200 hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-700 transition-all"
                                data-testid={`sims-dos-${membre.id}`}
                              >
                                <Calculator className="h-3.5 w-3.5 text-emerald-500" /> Simulations DOS
                              </Button>
                            )}
                          </div>

                          {/* Menu "Plus d'actions" — groupé par section */}
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-8 text-xs gap-1.5 w-full justify-center bg-gradient-to-r from-slate-50 to-white border-slate-200 hover:from-slate-100 hover:to-slate-50 transition-all"
                                data-testid={`more-actions-${membre.id}`}
                              >
                                <MoreHorizontal className="h-3.5 w-3.5" /> Plus d'actions
                                <ChevronDown className="h-3 w-3 opacity-60" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-60 p-1">
                              {/* Section: Gestion documents */}
                              {['super_admin', 'admin', 'cooper'].includes(userRole) && (
                                <>
                                  <div className="px-2 pt-1.5 pb-1 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Documents</div>
                                  <DropdownMenuItem onSelect={() => setUploadDocMembre(membre)} className="gap-2 cursor-pointer text-xs" data-testid={`upload-docs-${membre.id}`}>
                                    <Upload className="h-3.5 w-3.5 text-teal-500" /> Insérer des documents
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onSelect={() => setQualifsMembre(membre)} className="gap-2 cursor-pointer text-xs" data-testid={`qualifs-btn-${membre.id}`}>
                                    <Award className="h-3.5 w-3.5 text-amber-500" /> Qualifications professionnelles
                                  </DropdownMenuItem>
                                </>
                              )}
                              {['super_admin', 'admin', 'cooper'].includes(userRole) && (
                                <DropdownMenuItem onSelect={() => setSubDocsMembre(membre)} className="gap-2 cursor-pointer text-xs" data-testid={`sub-docs-${membre.id}`}>
                                  <FolderOpen className="h-3.5 w-3.5 text-indigo-500" /> Documents souscriptions
                                </DropdownMenuItem>
                              )}

                              {/* Section: Statuts & Certificats */}
                              {['super_admin', 'admin'].includes(userRole) && (
                                <>
                                  <DropdownMenuSeparator />
                                  <div className="px-2 pt-1.5 pb-1 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Statuts & Certificats</div>
                                  <DropdownMenuItem onSelect={() => setDispositifsMembre(membre)} className="gap-2 cursor-pointer text-xs" data-testid={`dispositifs-btn-${membre.id}`}>
                                    <Shield className="h-3.5 w-3.5 text-indigo-500" /> Statuts & Certificat
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onSelect={() => handleGenerateReleve(membre)}
                                    disabled={generatingReleveId === membre.id}
                                    className="gap-2 cursor-pointer text-xs"
                                    data-testid={`generate-releve-info-${membre.id}`}
                                  >
                                    {generatingReleveId === membre.id ? (
                                      <Loader2 className="h-3.5 w-3.5 text-blue-500 animate-spin" />
                                    ) : (
                                      <FileText className="h-3.5 w-3.5 text-blue-600" />
                                    )}
                                    Relevé d'information RCDS
                                    <QrCode className="h-3 w-3 ml-auto text-slate-400" />
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onSelect={() => handleGenerateReleveFtpe(membre)}
                                    disabled={generatingReleveFtpeId === membre.id}
                                    className="gap-2 cursor-pointer text-xs"
                                    data-testid={`generate-releve-ftpe-${membre.id}`}
                                  >
                                    {generatingReleveFtpeId === membre.id ? (
                                      <Loader2 className="h-3.5 w-3.5 text-emerald-500 animate-spin" />
                                    ) : (
                                      <FileText className="h-3.5 w-3.5 text-emerald-600" />
                                    )}
                                    Relevé d'information FTPE
                                    <QrCode className="h-3 w-3 ml-auto text-slate-400" />
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onSelect={() => setFtpeDateMembre(membre)} className="gap-2 cursor-pointer text-xs" data-testid={`ftpe-date-btn-${membre.id}`}>
                                    <Calendar className="h-3.5 w-3.5 text-amber-500" /> Date FTPE
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onSelect={() => setRemiseSolidaireMembre(membre)} className="gap-2 cursor-pointer text-xs" data-testid={`remise-solidaire-btn-${membre.id}`}>
                                    <Coins className="h-3.5 w-3.5 text-emerald-600" /> Remise solidaire négociée
                                  </DropdownMenuItem>
                                </>
                              )}

                              {/* Section: FTPE */}
                              {(showFTPE && membre.ftpe_actif) || (onFTPEAction && !membre.ftpe_actif) ? (
                                <>
                                  <DropdownMenuSeparator />
                                  <div className="px-2 pt-1.5 pb-1 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">FTPE</div>
                                  {showFTPE && membre.ftpe_actif && (
                                    <DropdownMenuItem onSelect={() => setSendFTPEMembre(membre)} className="gap-2 cursor-pointer text-xs">
                                      <Award className="h-3.5 w-3.5 text-amber-500" /> Attestation FTPE
                                    </DropdownMenuItem>
                                  )}
                                  {onFTPEAction && !membre.ftpe_actif && (
                                    <DropdownMenuItem onSelect={() => onFTPEAction(membre)} className="gap-2 cursor-pointer text-xs">
                                      <Star className="h-3.5 w-3.5 text-amber-500" /> Adhésion FTPE
                                    </DropdownMenuItem>
                                  )}
                                </>
                              ) : null}

                              {/* Section: Simulation rapide */}
                              {onCreateEstimation && (
                                <>
                                  <DropdownMenuSeparator />
                                  <div className="px-2 pt-1.5 pb-1 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Simulation</div>
                                  <DropdownMenuItem onSelect={() => onCreateEstimation(membre)} className="gap-2 cursor-pointer text-xs">
                                    <Download className="h-3.5 w-3.5 text-emerald-500" /> Estimation rapide
                                  </DropdownMenuItem>
                                </>
                              )}

                              {/* Section: Communication & Admin */}
                              {(onSendEmail || (userRole === 'super_admin') || (onImpersonate && ['admin', 'super_admin'].includes(userRole))) && (
                                <>
                                  <DropdownMenuSeparator />
                                  <div className="px-2 pt-1.5 pb-1 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">Administration</div>
                                  {userRole === 'super_admin' && (
                                    <DropdownMenuItem onSelect={() => setAssignCreateurMembre(membre)} className="gap-2 cursor-pointer text-xs" data-testid={`assign-createur-${membre.id}`}>
                                      <UserPlus className="h-3.5 w-3.5 text-purple-500" /> Assigner créateur
                                    </DropdownMenuItem>
                                  )}
                                  {onSendEmail && (
                                    <DropdownMenuItem onSelect={() => onSendEmail(membre)} className="gap-2 cursor-pointer text-xs">
                                      <Send className="h-3.5 w-3.5 text-blue-500" /> Envoyer un email
                                    </DropdownMenuItem>
                                  )}
                                  {onImpersonate && ['admin', 'super_admin'].includes(userRole) && (
                                    <DropdownMenuItem onSelect={() => onImpersonate(membre)} className="gap-2 cursor-pointer text-xs">
                                      <UserCog className="h-3.5 w-3.5 text-amber-500" /> Impersonate
                                    </DropdownMenuItem>
                                  )}
                                </>
                              )}

                              {/* Section: Danger */}
                              {onDelete && (
                                <>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem onSelect={() => onDelete(membre)} className="gap-2 cursor-pointer text-xs text-red-600 hover:!bg-red-50 hover:!text-red-700 focus:!bg-red-50 focus:!text-red-700">
                                    <Trash2 className="h-3.5 w-3.5" /> Supprimer le membre
                                  </DropdownMenuItem>
                                </>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      )}
                    </div>
                    {/* Documents inseres - apercu inline */}
                    {['super_admin', 'admin'].includes(userRole) && (
                      <MemberDocsInline membreId={mid} token={authToken} />
                    )}
                  </div>
                )}
              </div>
  );
}
